import { Component, inject, OnInit } from '@angular/core';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { RelatedAssetGroup } from '@app/core/model/entities/asset-group/asset-group';
import { AssetGroupsService } from '@app/features/main/views/asset-groups/asset-groups.service';
import {
  EntityChipsFieldBuilderComponent
} from '@app/shared/components/fields/chips-field-builder/entity-chips-field-builder/entity-chips-field-builder';
import { textContains } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'asset-groups-chips-field-builder',
  templateUrl: './entity-chips-field-builder.html',
  styleUrls: ['../chips-field-builder.component.scss'],
  standalone: false
})
export class AssetGroupsChipsFieldBuilderComponent extends EntityChipsFieldBuilderComponent<RelatedAssetGroup> implements OnInit {
  private assetGroupsService = inject(AssetGroupsService);

  protected readonly entityType = EntityTypeEnum.ASSET_GROUP;
  protected readonly viewPermission = PermissionEnum.VIEW_ASSET_GROUP;
  protected noRelatedEntitiesMessage = 'LABEL.NO_RELATED_ASSET_GROUPS';
  protected override requiresAsset = false;

  /**
   * Load asset groups next page.
   * @param startingIndex Start of the page to load.
   * @return Observable<RelatedAssetGroup[]> A list of RelatedAssetGroup matching the form filters.
   */
  protected loadEntitiesNextPage(startingIndex: number): Observable<RelatedAssetGroup[]> {
    const filters: Filter[] = [];
    if (this.form.controls.field.value) {
      filters.push(textContains('computedProperties.displayName', this.form.controls.field.value));
    }

    const selectParams: SelectParams = {
      locale: getLocale(),
      sorts: [
        {
          field: 'lastChangeDate',
          direction: 'DESC'
        }
      ],
      filters,
      start: startingIndex,
      count: this.appConfig.PREFERRED_REQUEST_PAGE_SIZE
    };

    return this.assetGroupsService.loadAssetGroupsPage(selectParams)
      .pipe(map(assetGroupsPage => assetGroupsPage.map(assetGroup => assetGroup.toRelatedAssetGroup())));
  }

  /**
   * Navigate to Asset Group sheet
   * @param relatedEntityId Asset Group ID
   */
  public async navigateToRelatedEntitySheet(relatedEntityId: string): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET_GROUP,
      relatedEntityId
    );
    await this.assetGroupsService.navigateToAssetGroupSheet(relatedEntityId);
  }
}
